import { ErrorCode, TriggerIgnoreReason } from '@bit-box/common';

export const TriggerIgnoreReasonCaption: Record<TriggerIgnoreReason, string> = {
  [TriggerIgnoreReason.IGNORED_BY_PRICE_CHANGE]:
    '최근 진입가(${lastOpenPrice})와 현재시세(${price})의 차이(${delta}) 변동률(${changePercent}%)이 ignorePriceChangePercent 설정(${ignorePriceChangePercent}%) 미만이므로 무시',
  [TriggerIgnoreReason.IGNORED_BY_ORDER_TYPE]: '수동, 익절, 손절 청산한 포지션과 같은 포지션 재시작 방지',
};

/**
 * ErrorCode에 대응하는 한국어 에러 메시지
 * {key} 형태의 플레이스홀더는 ctx 값으로 치환됩니다.
 */
export const ErrorMessages: Record<ErrorCode, string> = {
  // 기존 (Existing)
  [ErrorCode.ERR_FORM_INPUT_ERROR]: '입력값이 올바르지 않습니다.',
  [ErrorCode.ERR_POSITION_NOT_FOUND]: '{symbol} 청산할 포지션 정보가 DB에 없습니다.',
  [ErrorCode.ERR_FETCH_CANDLES]: '{symbol} {timeframe} 캔들 가져오기 실패',
  [ErrorCode.ERR_INSUFFICIENT_PROXIES]: '프록시 IP가 부족합니다. 필요: {requiredCount}개, 현재: {currentCount}개',
  [ErrorCode.ERR_PROXY_INVALID]:
    '프록시 검증 실패: {proxyIp} ({message}). 프록시 설정을 확인하고 올바른 프록시 IP를 입력해주세요.',

  // 거래소 (Exchange)
  [ErrorCode.ERR_EXCHANGE_NOT_SELECTED]: '거래소를 먼저 선택해주세요.',
  [ErrorCode.ERR_EXCHANGE_CONFIG_NOT_FOUND]: '거래소 설정 없음',
  [ErrorCode.ERR_EXCHANGE_CONFIG_READ_ERROR]: '설정 읽기 오류',
  [ErrorCode.ERR_EXCHANGE_INSTANCE_CREATE_ERROR]: '거래소 인스턴스 생성 오류',
  [ErrorCode.ERR_EXCHANGE_CONNECTION_ERROR]: '거래소 접속 중 오류가 발생했습니다.',
  [ErrorCode.ERR_POSITION_MODE_CHANGE_FAILED]:
    '포지션 모드 변경 실패. 열려 있는 포지션이 있으면 모드를 변경할 수 없습니다.',

  // 투자/세션 (Invest/Session)
  [ErrorCode.ERR_INVEST_ALREADY_RUNNING]: '이미 실행 중입니다.',
  [ErrorCode.ERR_INVEST_ALREADY_STOPPED]: '이미 중지된 상태입니다.',
  [ErrorCode.ERR_INVEST_CANNOT_MODIFY_WHILE_RUNNING]: '투자가 실행 중인 상태에서는 변경할 수 없습니다.',
  [ErrorCode.ERR_INVEST_NO_SYMBOLS]: '투자 후보 종목이 없습니다.',
  [ErrorCode.ERR_INVEST_SYMBOL_CHANGING]: '종목 변경 중입니다. 잠시 후 다시 시도해주세요.',
  [ErrorCode.ERR_INVEST_CANNOT_SWITCH_TAB]:
    '투자중에는 백테스트 탭으로 또는 백테스트 탭에서 다른 탭으로 이동할 수 없습니다.',
  [ErrorCode.ERR_INVEST_STOPPING]: '투자 중지중이므로 포지션 진입 주문은 불가능합니다.',
  [ErrorCode.ERR_BACKTEST_START_TIME_REQUIRED]: '백테스트 시작 시간을 먼저 선택해주세요.',
  [ErrorCode.ERR_INVEST_END_TIME_BEFORE_START]: '투자 종료 시간은 시작 시간({startAt})보다 커야 합니다.',
  [ErrorCode.ERR_INVEST_END_TIME_IN_PAST]: '투자 종료 시간은 현재 시간보다 이후여야 합니다.',

  // 포지션/주문 (Position/Order)
  [ErrorCode.ERR_POSITION_NOT_EXISTS]: '포지션이 없습니다.',
  [ErrorCode.ERR_SYMBOL_INFO_NOT_FOUND]: '종목 정보가 없습니다.',
  [ErrorCode.ERR_ENTRY_NOT_ALLOWED]: '{symbol} {posOrderType} 진입 불가능',
  [ErrorCode.ERR_ORDER_ALREADY_IN_PROGRESS]: '{symbol} 이미 진행중인 주문이 있음',
  [ErrorCode.ERR_MARKET_STATUS_NOT_FOUND]: '자산 관리 객체가 없으므로 취소',
  [ErrorCode.ERR_CANDLE_SERVICE_NOT_FOUND]: '{symbol}의 {timeframe} CandleService 없음',
  [ErrorCode.ERR_PRICE_NOT_FOUND]: '{symbol} 가격 없음',
  [ErrorCode.ERR_ORDERBOOK_NOT_FOUND]: '오더북 데이터가 없음',
  [ErrorCode.ERR_NO_TRADABLE_AMOUNT]: '거래 가능 금액이 없음',
  [ErrorCode.ERR_VIRTUAL_ORDER_FAILED]: '{symbol} 가상 주문 처리 실패',
  [ErrorCode.ERR_POSITION_SIZE_NOT_CHANGED]: '{symbol} 포지션 크기 변경되지 않음',
  [ErrorCode.ERR_ORDER_CANCELED]: '주문 상태: {status}. 주문 ID: {orderId}',
  [ErrorCode.ERR_SYMBOL_AMOUNT_EMPTY]: '종목 수량 없음',

  // 잔고 (Balance)
  [ErrorCode.ERR_VIRTUAL_BALANCE_TOO_LOW]: '가상 잔고는 {minBalance} 이상이어야 합니다.',
  [ErrorCode.ERR_INVEST_MODE_MISMATCH]: '투자 모드가 일치하지 않습니다.',
  [ErrorCode.ERR_WITHDRAW_WHILE_STOPPED]: '봇 중지 중에는 수익 회수 불가',
  [ErrorCode.ERR_SEED_REQUIRED]: '세션 시작 시 씨드를 먼저 설정해주세요.',
  [ErrorCode.ERR_WITHDRAW_EXCEED_MAX]: '회수 가능한 최대 금액은 {maxAmount} 입니다.',
  [ErrorCode.ERR_COST_BELOW_MIN]: '할당된 투자금이 최저 Cost 제한({minCost}) 미만',
  [ErrorCode.ERR_COST_EXCEED_MAX]: '할당된 투자금이 최대 Cost 제한({maxCost}) 초과',
  [ErrorCode.ERR_INSUFFICIENT_INVESTABLE]: '진입 가능 금액({investable}) 부족. 투자금({quoteAmt})',

  // 전략 (Strategy)
  [ErrorCode.ERR_STRATEGY_CONFIG_READ_ERROR]: '설정 읽기 오류',
  [ErrorCode.ERR_STRATEGY_LOG_READ_ERROR]: '로그 읽기 오류',
  [ErrorCode.ERR_NO_TIMEFRAMES]: '매매 지표 생성 주기가 없습니다.',
  [ErrorCode.ERR_UNSUPPORTED_TRIGGER_TYPE]: '{triggerType} 트리거 타입은 지원되지 않습니다.',
  [ErrorCode.ERR_CONDITION_NOT_FOUND]: '조건을 찾을 수 없습니다. (id={conditionId}, name={conditionName})',
  [ErrorCode.ERR_SIGNAL_TYPE_NOT_FOUND]: '{signalTypeId}번 신호 타입을 찾을 수 없습니다.',
  [ErrorCode.ERR_INDICATOR_NOT_FOUND]: '{indicatorId}번 지표를 찾을 수 없습니다.',
  [ErrorCode.ERR_CONDITION_REF_INVALID_INDICATOR]: '조건이 존재하지 않는 지표 "{indicatorIds}"를 참조하고 있습니다.',
  [ErrorCode.ERR_CONDITION_REF_INVALID_SIGNAL_TYPE]:
    '조건이 존재하지 않는 신호 타입 "{signalTypeIds}"를 참조하고 있습니다.',
  [ErrorCode.ERR_CHANNEL_CONDITION_OPERANDS]: '채널 비교 조건은 연산항이 3개여야 합니다.',
  [ErrorCode.ERR_USAGE_MIN_ONE]: '[용도]는 최소 한 개 이상이어야 합니다.',
  [ErrorCode.ERR_NAME_REQUIRED]: '전략명을 입력해 주세요',
  [ErrorCode.ERR_NAME_DUPLICATE]: '이름 "{name}"이 이미 존재합니다.',
  [ErrorCode.ERR_REQUIRED_SIGNAL_TYPE_MODIFY]: '필수 신호 타입 수정 불가',
  [ErrorCode.ERR_REQUIRED_SIGNAL_TYPE_DELETE]: '필수 신호 타입 삭제 불가',
  [ErrorCode.ERR_STRATEGY_OR_ID_REQUIRED]: 'strategyConfig 또는 strategyId가 필요합니다.',

  //분석 (Analysis)
  [ErrorCode.ERR_CORRELATION_CALC_CANCELLED]: '상관계수 계산이 취소되었습니다.',

  // 인증 (Auth)
  [ErrorCode.ERR_ALREADY_LOGGED_IN_OTHER_USER]: '이미 다른 유저로 로그인되어 있습니다.',
  [ErrorCode.ERR_ALREADY_LOGGED_IN_OTHER_LICENSE]: '이미 다른 이용권으로 로그인되어 있습니다.',
  [ErrorCode.ERR_API_KEY_REQUIRED]: '거래소 API 키를 먼저 등록해주세요.',

  // 기타 (Misc)
  [ErrorCode.ERR_TELEGRAM_API_FAILED]: '텔레그램 API 호출 실패',
  [ErrorCode.ERR_TELEGRAM_NO_CHAT]: '채팅방이 없습니다. 텔레그램 채팅방에 봇을 추가하고 /start 명령을 입력해주세요.',
  [ErrorCode.ERR_TELEGRAM_NO_TOKEN]: '텔레그램 API 토큰이 비어있습니다.',
  [ErrorCode.ERR_MUTEX_ALREADY_LOCKED]: '이미 잠금 상태입니다.',
  [ErrorCode.ERR_CANDLE_FETCH_MAIN_ONLY]: '캔들 가져오기 실패. 캔들 가져오기는 메인 프로세스에서만 가능합니다.',
  [ErrorCode.ERR_INDICATOR_BUFFER_INSUFFICIENT]: '버퍼 캔들이 부족하여 지표 계산 실패',
  [ErrorCode.ERR_TALIB_FUNCTION_NOT_FOUND]: '기술 분석 함수를 찾지 못함: {funcId}',
  [ErrorCode.ERR_EXCHANGE_SERVICE_NOT_INIT]: 'Exchange service가 초기화되지 않았습니다.',
  [ErrorCode.ERR_IMMEDIATE_LIQUIDATION_RISK]: '즉각적인 청산 위험이 있습니다. 현재가: {markPrice}, 청산가: {liqPrice}',
  [ErrorCode.ERR_CONDITION_SYMBOL_REQUIRED]: '도형/웹훅을 참조하는 조건에는 종목이 필요합니다.',
};
